import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { supabase } from '../../lib/supabaseClient';
import { parseHoursCapError } from '../../lib/hoursCapError';

/* ── Calendar constants ────────────────────────────── */
const SLOTS_PER_DAY = 48;           // 30-min blocks (00:00 → 23:30)
const SLOT_HEIGHT = 26;              // px per half-hour row
const DAY_LABELS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const WORKING_START_SLOT = 18;       // 09:00
const WORKING_END_SLOT = 36;         // 18:00
const INITIAL_SCROLL_SLOT = 16;      // scroll to 08:00 on mount

/* Internal requirement statuses that close out a requirement — these are
   excluded from the time-log requirement picker (no new activities allowed). */
const CLOSED_REQUIREMENT_STATUSES = new Set(['Entregado', 'Cancelado']);

/* Activity color palette — lightness variations derived from brand colors */
const HOUR_TYPE_PALETTES = {
  'Facturables':     ['#00CFC1', '#00A39A', '#1AB5A8', '#007A73', '#33D9CE', '#005A54', '#4DDFD2', '#008C83'],
  'No Facturables':  ['#00115D', '#1A2A72', '#334087', '#000742', '#4D559C', '#0A1969', '#5E64AA', '#2D3B80'],
  'N/A':             ['#2E0055', '#4A1B72', '#66378E', '#1D0038', '#8254AA', '#3D1164', '#9F70C6', '#572384'],
};
function colorForActivity(activity) {
  if (!activity) return '#666';
  const palette = HOUR_TYPE_PALETTES[activity.hour_types?.name] || HOUR_TYPE_PALETTES['Facturables'];
  return palette[activity.id % palette.length];
}

/* ── Date / time helpers ───────────────────────────── */
function mondayOfWeek(d) {
  const nd = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const day = nd.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  nd.setDate(nd.getDate() + diff);
  return nd;
}
function addDays(d, n) {
  const nd = new Date(d);
  nd.setDate(nd.getDate() + n);
  return nd;
}
function dateStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function slotToTime(idx) {
  const hr = Math.floor(idx / 2);
  const mn = (idx % 2) * 30;
  return `${String(hr).padStart(2, '0')}:${String(mn).padStart(2, '0')}:00`;
}
function slotEndTime(nextSlot) {
  // '24:00:00' is a valid Postgres time; '23:59:59' would violate the
  // time_logs_half_hour_aligned CHECK (minutes must be :00/:30, seconds 0).
  if (nextSlot >= SLOTS_PER_DAY) return '24:00:00';
  return slotToTime(nextSlot);
}
function timeToSlot(time) {
  const [h, m] = time.split(':').map(Number);
  return h * 2 + Math.floor(m / 30);
}
function isWeekendDateStr(dateStrVal) {
  const day = new Date(dateStrVal + 'T00:00:00').getDay();
  return day === 0 || day === 6;
}
function isExtendedSlot(dateStrVal, idx) {
  // Weekends count entirely as extended hours, regardless of time of day
  if (isWeekendDateStr(dateStrVal)) return true;
  return idx < WORKING_START_SLOT || idx >= WORKING_END_SLOT;
}
function formatWeekRange(monday) {
  const sunday = addDays(monday, 6);
  const sameMonth = monday.getMonth() === sunday.getMonth();
  if (sameMonth) {
    return `Semana del ${monday.getDate()} al ${sunday.toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' })}`;
  }
  return `Semana del ${monday.toLocaleDateString('es-MX', { day: 'numeric', month: 'long' })} al ${sunday.toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' })}`;
}
function formatEntryDate(dateStrVal) {
  const d = new Date(dateStrVal + 'T00:00:00');
  return d.toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' });
}
function hexToRgba(hex, alpha) {
  const c = hex.replace('#', '');
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

/* ──────────────────────────────────────────────────── */
function TimeLogForm({ userId, role, onBack }) {
  const isManagerOrLeader = role === 'Gerente' || role === 'Líder';
  // Only Gerente is exempt from the past-day lock. Líder & Desarrollador can
  // only write today → end of week, unless granted a per-day permission.
  const isGerente = role === 'Gerente';

  // Week — `monday` stays anchored to the *current* week (the rollover and
  // save-time checks compare against it). A Gerente may step back exactly one
  // week (pre-report corrections); `viewMonday` is the week actually shown.
  const [monday, setMonday] = useState(() => mondayOfWeek(new Date()));
  const [weekOffset, setWeekOffset] = useState(0); // 0 = current, -1 = previous (Gerente only)
  const isPrevWeek = weekOffset === -1;
  const viewMonday = useMemo(() => addDays(monday, weekOffset * 7), [monday, weekOffset]);
  const weekDates = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(viewMonday, i)),
    [viewMonday]
  );
  const weekDateStrs = useMemo(() => weekDates.map(dateStr), [weekDates]);

  // Target user (self by default; Líder/Gerente can pick another)
  const [targetUserId, setTargetUserId] = useState(null);
  useEffect(() => {
    if (userId && !targetUserId) setTargetUserId(userId);
  }, [userId, targetUserId]);

  // Catalogs
  const [activities, setActivities] = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [allUsers, setAllUsers] = useState([]);

  // Selection state
  const [selectedRequirementId, setSelectedRequirementId] = useState(''); // '' | 'none' | uuid
  const [selectedActivityId, setSelectedActivityId] = useState(null);

  // Painted blocks: Map<`${date}_${slot}`, { activityId }>
  const [blocks, setBlocks] = useState(() => new Map());

  // Existing (previously saved) logs for this week
  const [existingLogs, setExistingLogs] = useState([]);

  // Painting interaction
  const [isPainting, setIsPainting] = useState(false);
  const paintModeRef = useRef('add');

  // Modals
  const [pendingReqChange, setPendingReqChange] = useState(null);
  const [showDescModal, setShowDescModal] = useState(false);
  const [entriesToSave, setEntriesToSave] = useState([]);
  const [descriptions, setDescriptions] = useState({});

  // Messages / flags
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [showExtBlockedModal, setShowExtBlockedModal] = useState(false);
  // null | 'no-target-user' | 'no-assignments' | 'no-req' | 'no-activity'
  const [guideModal, setGuideModal] = useState(null);
  // null | { type: 'null_cap'|'exceeded', refId, requirement, cap?, consumed?, incoming?, remaining? }
  const [hoursCapModal, setHoursCapModal] = useState(null);

  // Past-day lock (Líder & Desarrollador): days of this week the target user
  // may still edit despite being in the past (active grants), + info modal flag.
  const [grantedDays, setGrantedDays] = useState(() => new Set());
  const [showLockedModal, setShowLockedModal] = useState(false);
  // Bumped every minute so "today" (and thus the locked set) recomputes across
  // the midnight rollover and reflects grant expiry without a manual refresh.
  const [minuteTick, setMinuteTick] = useState(0);

  // Delete flow
  const [pendingDelete, setPendingDelete] = useState(null); // log object
  const [deleting, setDeleting] = useState(false);
  const [deleteErrorMsg, setDeleteErrorMsg] = useState('');

  // Mode switcher: 'create' | 'delete' | 'edit'
  const [calMode, setCalMode] = useState('create');
  const [pendingModeChange, setPendingModeChange] = useState(null); // target mode if blocks exist
  const [pendingWeekChange, setPendingWeekChange] = useState(null); // target week offset if blocks exist

  // Edit (resize) flow
  const [resizingLog, setResizingLog] = useState(null);     // log being resized
  const [resizeHandle, setResizeHandle] = useState(null);   // 'top' | 'bottom'
  const [resizePreviewSlot, setResizePreviewSlot] = useState(null);
  const [pendingEdit, setPendingEdit] = useState(null);     // { log, newStartSlot, newEndSlot, newTotalHours, newExtendedHours }
  const [editSaving, setEditSaving] = useState(false);
  const [editErrorMsg, setEditErrorMsg] = useState('');

  const scrollerRef = useRef(null);

  // Refs for stable access inside global event listeners (assigned every render)
  const resizeStateRef = useRef({ resizingLog: null, resizeHandle: null, resizePreviewSlot: null });
  resizeStateRef.current = { resizingLog, resizeHandle, resizePreviewSlot };
  const existingLogsRef = useRef([]);
  existingLogsRef.current = existingLogs;
  const onResizeEndRef = useRef(null);

  /* ── Load catalogs ── */
  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from('activities')
        .select('id, name, hour_type_id, hour_types(id, name)')
        .eq('active', true)
        .order('name');
      setActivities(data || []);
    })();
  }, []);

  useEffect(() => {
    if (!isManagerOrLeader) return;
    (async () => {
      const { data } = await supabase
        .from('users')
        .select('id, full_name')
        .eq('active', true)
        .order('full_name');
      setAllUsers(data || []);
    })();
  }, [isManagerOrLeader]);

  /* ── Load requirements for target user ──
     A user can log time against a requirement where they are (a) assigned as a
     developer (`requirement_devs`) OR (b) the estimator (`estimator_user_id`).
     Both sources are merged and de-duplicated. The backend time_logs
     validations (hours cap, extended-hours permission, activity/requirement
     pairing) are requirement-based, not assignment-based, so estimator-logged
     entries are validated identically to dev-logged ones. */
  useEffect(() => {
    if (!targetUserId) return;
    (async () => {
      const REQ_COLS = 'id, reference_id, description, allow_extended_hours, estimated_completion_hours_dev, status:status!status_id(name), leader:users!leader_user_id(id, full_name)';
      const [devRes, estRes] = await Promise.all([
        supabase
          .from('requirement_devs')
          .select(`requirement_id, requirement:requirements!requirement_id(${REQ_COLS})`)
          .eq('user_id', targetUserId),
        supabase
          .from('requirements')
          .select(REQ_COLS)
          .eq('estimator_user_id', targetUserId),
      ]);
      const devReqs = (devRes.data || []).map((r) => r.requirement).filter(Boolean);
      const estReqs = estRes.data || [];
      // Hide closed requirements (internal status, not client status): once a
      // requirement is Entregado or Cancelado it should no longer be selectable
      // for new time logs.
      const reqs = [...devReqs, ...estReqs]
        .filter((r) => r && !CLOSED_REQUIREMENT_STATUSES.has(r.status?.name));
      const seen = new Set();
      const unique = reqs.filter((r) => {
        if (seen.has(r.id)) return false;
        seen.add(r.id);
        return true;
      });
      unique.sort((a, b) => a.reference_id.localeCompare(b.reference_id));
      setRequirements(unique);
    })();
  }, [targetUserId]);

  /* ── Load existing logs for the week ── */
  const fetchExistingLogs = useCallback(async () => {
    if (!targetUserId) return;
    const { data } = await supabase
      .from('time_logs')
      .select('id, log_date, start_time, end_time, total_hours, extended_hours, activity:activities!activity_id(id, name, hour_types(name)), requirement:requirements!requirement_id(id, reference_id, description, allow_extended_hours)')
      .eq('user_id', targetUserId)
      .gte('log_date', weekDateStrs[0])
      .lte('log_date', weekDateStrs[6]);
    setExistingLogs(data || []);
  }, [targetUserId, weekDateStrs]);

  useEffect(() => {
    fetchExistingLogs();
  }, [fetchExistingLogs]);

  /* ── Active per-day grants for the target user (un-lock past days) ── */
  const fetchGrantedDays = useCallback(async () => {
    if (!targetUserId || isGerente) {
      setGrantedDays(new Set());
      return;
    }
    const { data } = await supabase
      .from('time_log_permissions')
      .select('log_date')
      .eq('user_id', targetUserId)
      .is('revoked_at', null)
      .gt('expires_at', new Date().toISOString())
      .gte('log_date', weekDateStrs[0])
      .lte('log_date', weekDateStrs[6]);
    setGrantedDays(new Set((data || []).map((r) => r.log_date)));
  }, [targetUserId, isGerente, weekDateStrs]);

  // Fail-closed: the instant the target user (or week) changes, drop any grants
  // from the previous selection so a stale grant can never transiently un-lock a
  // day for a different user while the async refetch is in flight. (Runs before
  // the fetch below resolves; the minute refresh does NOT clear, to avoid a
  // 60s flicker on legitimately-granted days.)
  useEffect(() => {
    setGrantedDays(new Set());
  }, [targetUserId, weekDateStrs]);

  // Refetch on target/week change AND every minute (minuteTick) so expiry/revoke
  // and the midnight rollover are reflected.
  useEffect(() => {
    fetchGrantedDays();
  }, [fetchGrantedDays, minuteTick]);

  /* ── Minute tick (midnight rollover + grant expiry) ── */
  useEffect(() => {
    const id = setInterval(() => setMinuteTick((t) => t + 1), 60000);
    return () => clearInterval(id);
  }, []);

  /* ── "Today" (MX/local) and the locked-day set ── */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const todayStrVal = useMemo(() => dateStr(new Date()), [minuteTick]);
  const lockedDays = useMemo(() => {
    const s = new Set();
    if (isGerente) return s;
    for (const dstr of weekDateStrs) {
      if (dstr < todayStrVal && !grantedDays.has(dstr)) s.add(dstr);
    }
    return s;
  }, [isGerente, weekDateStrs, todayStrVal, grantedDays]);

  /* ── Scroll to 08:00 on mount ── */
  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = INITIAL_SCROLL_SLOT * SLOT_HEIGHT;
    }
  }, []);

  /* ── Global mouseup to end painting and resize ── */
  useEffect(() => {
    const onUp = () => {
      setIsPainting(false);
      onResizeEndRef.current?.();
    };
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchend', onUp);
    };
  }, []);

  /* ── Refresh "today" every minute to catch week rollover ── */
  useEffect(() => {
    const id = setInterval(() => {
      const currentMonday = mondayOfWeek(new Date());
      if (currentMonday.getTime() !== monday.getTime()) {
        setMonday(currentMonday);
        setWeekOffset(0);
        setBlocks(new Map());
        setSelectedRequirementId('');
        setSelectedActivityId(null);
        setCalMode('create');
        setResizingLog(null);
        setResizeHandle(null);
        setResizePreviewSlot(null);
        setPendingEdit(null);
        document.body.classList.remove('act-resizing');
        setErrorMsg('La semana cambió. Ya no es posible modificar los logs de semanas pasadas, recarga la vista.');
      }
    }, 60000);
    return () => clearInterval(id);
  }, [monday]);

  /* ── Occupied slot lookup (existing logs) ── */
  const occupiedSlots = useMemo(() => {
    const m = new Map();
    for (const log of existingLogs) {
      const start = timeToSlot(log.start_time);
      const end = timeToSlot(log.end_time);
      const safeEnd = end <= start ? SLOTS_PER_DAY : end;
      for (let s = start; s < safeEnd; s++) {
        m.set(`${log.log_date}_${s}`, log);
      }
    }
    return m;
  }, [existingLogs]);

  /* ── Grouped activities for palette ── */
  const groupedActivities = useMemo(() => {
    const groups = {};
    for (const a of activities) {
      const g = a.hour_types?.name || 'Otros';
      if (!groups[g]) groups[g] = [];
      groups[g].push(a);
    }
    return groups;
  }, [activities]);

  const isNoneRequirement = selectedRequirementId === 'none';

  /* Whether the currently selected requirement/mode permits painting outside working hours */
  const extendedHoursAllowed = useMemo(() => {
    if (!selectedRequirementId) return false;
    if (isNoneRequirement) return false; // N/A activities never allow extended hours
    const req = requirements.find((r) => r.id === selectedRequirementId);
    return req?.allow_extended_hours === true;
  }, [selectedRequirementId, isNoneRequirement, requirements]);
  const visibleActivityGroups = useMemo(() => {
    if (isNoneRequirement) return { 'N/A': groupedActivities['N/A'] || [] };
    const { 'N/A': _excluded, ...rest } = groupedActivities;
    return rest;
  }, [groupedActivities, isNoneRequirement]);

  /* ── Totals ── */
  const totals = useMemo(() => {
    let normal = 0;
    let ext = 0;
    for (const [key] of blocks) {
      const [date, slotStr] = key.split('_');
      if (isExtendedSlot(date, Number(slotStr))) ext += 0.5;
      else normal += 0.5;
    }
    return { normal, ext, total: normal + ext };
  }, [blocks]);

  /* ── Edit preview: compute clamped resize result while dragging ── */
  const editPreview = useMemo(() => {
    if (!resizingLog || resizePreviewSlot === null) return null;
    const logStartSlot = timeToSlot(resizingLog.start_time);
    const logEndSlot = timeToSlot(resizingLog.end_time) - 1;
    const otherLogs = existingLogs.filter((l) => l.id !== resizingLog.id && l.log_date === resizingLog.log_date);
    const allowExt = resizingLog.requirement?.allow_extended_hours === true;
    // Weekend logs are 100% extended: the working-window clamp doesn't apply
    // (resizing them without the permission is blocked at handle-down).
    const weekendLog = isWeekendDateStr(resizingLog.log_date);

    if (resizeHandle === 'bottom') {
      let maxSlot = SLOTS_PER_DAY - 1;
      for (const other of otherLogs) {
        const otherStart = timeToSlot(other.start_time);
        if (otherStart > logStartSlot) maxSlot = Math.min(maxSlot, otherStart - 1);
      }
      if (!allowExt && !weekendLog) maxSlot = Math.min(maxSlot, WORKING_END_SLOT - 1);
      const clamped = Math.max(logStartSlot, Math.min(maxSlot, resizePreviewSlot));
      return { log: resizingLog, newStartSlot: logStartSlot, newEndSlot: clamped };
    } else {
      let minSlot = 0;
      for (const other of otherLogs) {
        const otherEndExclusive = timeToSlot(other.end_time);
        if (otherEndExclusive <= logStartSlot) minSlot = Math.max(minSlot, otherEndExclusive);
      }
      if (!allowExt && !weekendLog) minSlot = Math.max(minSlot, WORKING_START_SLOT);
      const clamped = Math.max(minSlot, Math.min(logEndSlot, resizePreviewSlot));
      return { log: resizingLog, newStartSlot: clamped, newEndSlot: logEndSlot };
    }
  }, [resizingLog, resizeHandle, resizePreviewSlot, existingLogs]);

  /* ── Per-slot preview type: 'keep' | 'trim' | 'expand' ── */
  const editPreviewSlots = useMemo(() => {
    if (!editPreview) return null;
    const { log, newStartSlot, newEndSlot } = editPreview;
    const origStart = timeToSlot(log.start_time);
    const origEnd = timeToSlot(log.end_time) - 1;
    const result = new Map();
    const minS = Math.min(origStart, newStartSlot);
    const maxS = Math.max(origEnd, newEndSlot);
    for (let s = minS; s <= maxS; s++) {
      const inOrig = s >= origStart && s <= origEnd;
      const inNew = s >= newStartSlot && s <= newEndSlot;
      if (inOrig && inNew) result.set(`${log.log_date}_${s}`, 'keep');
      else if (inOrig) result.set(`${log.log_date}_${s}`, 'trim');
      else if (inNew) result.set(`${log.log_date}_${s}`, 'expand');
    }
    return result;
  }, [editPreview]);

  /* ── Interaction handlers ── */
  function handleRequirementClick(reqId) {
    if (reqId === selectedRequirementId) return;
    if (blocks.size > 0) {
      setPendingReqChange(reqId);
      return;
    }
    setSelectedRequirementId(reqId);
    setSelectedActivityId(null);
  }

  function confirmRequirementChange() {
    setBlocks(new Map());
    setSelectedRequirementId(pendingReqChange);
    setSelectedActivityId(null);
    setPendingReqChange(null);
  }

  function handleUserChange(newUid) {
    if (blocks.size > 0) {
      if (!window.confirm('Se descartarán los bloques pintados. ¿Continuar?')) return;
    }
    setBlocks(new Map());
    setSelectedRequirementId('');
    setSelectedActivityId(null);
    setTargetUserId(newUid);
  }

  function clearAll() {
    if (blocks.size === 0) return;
    if (window.confirm('¿Descartar todos los bloques pintados?')) {
      setBlocks(new Map());
    }
  }

  function handleModeSwitch(targetMode) {
    if (targetMode === calMode) return;
    if (calMode === 'create' && blocks.size > 0) {
      setPendingModeChange(targetMode);
      return;
    }
    // Clear any in-flight resize when switching modes
    setResizingLog(null);
    setResizeHandle(null);
    setResizePreviewSlot(null);
    setPendingEdit(null);
    setEditErrorMsg('');
    document.body.classList.remove('act-resizing');
    setCalMode(targetMode);
  }

  function applyModeSwitch() {
    if (!pendingModeChange) return;
    setBlocks(new Map());
    setCalMode(pendingModeChange);
    setPendingModeChange(null);
  }

  /* ── Week navigation (Gerente only: current ↔ previous week) ── */
  function handleWeekNav(offset) {
    if (!isGerente || offset === weekOffset) return;
    if (blocks.size > 0) {
      setPendingWeekChange(offset);
      return;
    }
    applyWeekNav(offset);
  }

  function applyWeekNav(offset) {
    setBlocks(new Map());
    setPendingWeekChange(null);
    setResizingLog(null);
    setResizeHandle(null);
    setResizePreviewSlot(null);
    setPendingEdit(null);
    setEditErrorMsg('');
    setPendingDelete(null);
    setDeleteErrorMsg('');
    document.body.classList.remove('act-resizing');
    setWeekOffset(offset);
  }

  /* ── Edit handle mousedown ── */
  function handleEditHandleDown(e, log, handle) {
    e.preventDefault();
    e.stopPropagation();
    if (lockedDays.has(log.log_date)) {
      setShowLockedModal(true);
      return;
    }
    // Weekend logs are 100% extended hours — resizing one requires the
    // requirement's extended-hours permission (it may have been revoked
    // after the log was created).
    if (isWeekendDateStr(log.log_date) && log.requirement?.allow_extended_hours !== true) {
      setShowExtBlockedModal(true);
      return;
    }
    document.body.classList.add('act-resizing');
    setResizingLog(log);
    setResizeHandle(handle);
    const initSlot = handle === 'bottom'
      ? timeToSlot(log.end_time) - 1
      : timeToSlot(log.start_time);
    setResizePreviewSlot(initSlot);
  }

  /* ── handleResizeEnd — called from global mouseup via ref ── */
  function handleResizeEnd() {
    document.body.classList.remove('act-resizing');
    const { resizingLog: rLog, resizeHandle: rHandle, resizePreviewSlot: rSlot } = resizeStateRef.current;

    setResizingLog(null);
    setResizeHandle(null);
    setResizePreviewSlot(null);

    if (!rLog || rSlot === null) return;

    const logStartSlot = timeToSlot(rLog.start_time);
    const logEndSlot = timeToSlot(rLog.end_time) - 1;
    const otherLogs = existingLogsRef.current.filter((l) => l.id !== rLog.id && l.log_date === rLog.log_date);
    const allowExt = rLog.requirement?.allow_extended_hours === true;
    const weekendLog = isWeekendDateStr(rLog.log_date);

    let newStartSlot, newEndSlot;
    if (rHandle === 'bottom') {
      let maxSlot = SLOTS_PER_DAY - 1;
      for (const other of otherLogs) {
        const otherStart = timeToSlot(other.start_time);
        if (otherStart > logStartSlot) maxSlot = Math.min(maxSlot, otherStart - 1);
      }
      if (!allowExt && !weekendLog) maxSlot = Math.min(maxSlot, WORKING_END_SLOT - 1);
      const clamped = Math.max(logStartSlot, Math.min(maxSlot, rSlot));
      newStartSlot = logStartSlot;
      newEndSlot = clamped;
    } else {
      let minSlot = 0;
      for (const other of otherLogs) {
        const otherEndExclusive = timeToSlot(other.end_time);
        if (otherEndExclusive <= logStartSlot) minSlot = Math.max(minSlot, otherEndExclusive);
      }
      if (!allowExt && !weekendLog) minSlot = Math.max(minSlot, WORKING_START_SLOT);
      const clamped = Math.max(minSlot, Math.min(logEndSlot, rSlot));
      newStartSlot = clamped;
      newEndSlot = logEndSlot;
    }

    // No-op if position unchanged
    if (newStartSlot === logStartSlot && newEndSlot === logEndSlot) return;

    let newTotalHours = 0;
    let newExtendedHours = 0;
    for (let s = newStartSlot; s <= newEndSlot; s++) {
      if (isExtendedSlot(rLog.log_date, s)) newExtendedHours += 0.5;
      else newTotalHours += 0.5;
    }

    setPendingEdit({ log: rLog, newStartSlot, newEndSlot, newTotalHours, newExtendedHours });
  }
  // Keep the ref in sync so the global mouseup listener always calls the latest version
  onResizeEndRef.current = handleResizeEnd;

  /* ── Confirm edit (Supabase UPDATE) ── */
  async function handleConfirmEdit() {
    if (!pendingEdit) return;
    const { log, newStartSlot, newEndSlot, newTotalHours, newExtendedHours } = pendingEdit;

    // Locked past day (no active grant) — blocked for Líder/Desarrollador
    if (!isGerente && lockedDays.has(log.log_date)) {
      setEditErrorMsg('Este día ya pasó y está bloqueado. Solicita permiso a un Gerente para modificarlo.');
      return;
    }

    // Week rollover check (Desarrollador — RLS also enforces this server-side)
    if (!isManagerOrLeader && mondayOfWeek(new Date()).getTime() !== monday.getTime()) {
      setEditErrorMsg('La semana actual cambió. Ya no es posible modificar registros de semanas anteriores.');
      return;
    }

    // Extended hours check (early feedback; trg_enforce_extended_hours_permission also enforces this on UPDATE)
    if (newExtendedHours > 0 && !log.requirement?.allow_extended_hours) {
      setEditErrorMsg('Este requerimiento no tiene autorización para registrar horas fuera del horario laboral.');
      return;
    }

    setEditSaving(true);

    // 24h/day cap: fetch current day totals and exclude this log
    const { data: dayLogs, error: dayErr } = await supabase
      .from('time_logs')
      .select('id, total_hours, extended_hours, start_time, end_time')
      .eq('user_id', targetUserId)
      .eq('log_date', log.log_date);

    if (dayErr) {
      setEditSaving(false);
      setEditErrorMsg(dayErr.message || 'Error al validar');
      return;
    }

    const otherDayLogs = (dayLogs || []).filter((l) => l.id !== log.id);
    const otherHours = otherDayLogs
      .reduce((sum, l) => sum + (l.total_hours || 0) + (l.extended_hours || 0), 0);

    if (otherHours + newTotalHours + newExtendedHours > 24) {
      setEditSaving(false);
      setEditErrorMsg(`Excede el límite de 24h en este día (${otherHours.toFixed(1)}h en otros registros)`);
      return;
    }

    // Overlap re-check: the resize was clamped against the locally cached
    // logs, which may be stale if another session wrote logs for this user.
    const editConflict = otherDayLogs.find((l) => {
      const oStart = timeToSlot(l.start_time);
      const oEnd = timeToSlot(l.end_time);
      return newStartSlot < oEnd && newEndSlot + 1 > oStart;
    });
    if (editConflict) {
      setEditSaving(false);
      setEditErrorMsg(
        `El nuevo horario se superpone con otro registro (${editConflict.start_time.slice(0, 5)}–${editConflict.end_time.slice(0, 5)}). El calendario se actualizó.`
      );
      fetchExistingLogs();
      return;
    }

    // Requirement hours cap check for resized log — only applies to Facturable activities
    const editReqId = pendingEdit.log.requirement?.id;
    const editIsFacturable = pendingEdit.log.activity?.hour_types?.name === 'Facturables';
    if (editReqId && editIsFacturable) {
      const req = requirements.find((r) => r.id === editReqId);
      if (req !== undefined) {
        const capVal = req.estimated_completion_hours_dev;
        if (capVal === null) {
          setEditSaving(false);
          setEditErrorMsg(
            `El requerimiento ${req.reference_id} no tiene horas de desarrollo estimadas. ` +
            `Contacta a ${req.leader?.full_name || 'tu líder'} para asignar las horas disponibles.`
          );
          return;
        }
        if (capVal !== undefined) {
          const { data: reqLogs } = await supabase
            .from('time_logs')
            .select('id, total_hours, extended_hours, activity:activities!activity_id(hour_types(name))')
            .eq('requirement_id', editReqId);
          const consumed = (reqLogs || [])
            .filter((l) => l.id !== pendingEdit.log.id && l.activity?.hour_types?.name === 'Facturables')
            .reduce((sum, l) => sum + (l.total_hours || 0) + (l.extended_hours || 0), 0);
          const incoming = newTotalHours + newExtendedHours;
          const cap = Number(capVal);
          if (consumed + incoming > cap) {
            setEditSaving(false);
            setEditErrorMsg(
              `Excede el límite del requerimiento (${cap}h total). ` +
              `Disponible: ${Math.max(0, cap - consumed).toFixed(1)}h, intentas usar: ${incoming.toFixed(1)}h.`
            );
            return;
          }
        }
      }
    }

    const newStartTime = slotToTime(newStartSlot);
    const newEndTime = slotEndTime(newEndSlot + 1);
    const isExtended = newExtendedHours > 0;

    const { error, count } = await supabase
      .from('time_logs')
      .update({
        start_time: newStartTime,
        end_time: newEndTime,
        total_hours: newTotalHours,
        extended_hours: isExtended ? newExtendedHours : null,
        is_extended_hours: isExtended,
      }, { count: 'exact' })
      .eq('id', log.id);

    setEditSaving(false);

    if (error) {
      const capErr = parseHoursCapError(error.message);
      if (capErr) {
        if (capErr.type === 'null_cap') {
          const req = requirements.find((r) => r.reference_id === capErr.refId);
          setEditErrorMsg(
            `El requerimiento ${capErr.refId} no tiene horas estimadas. ` +
            `Contacta a ${req?.leader?.full_name || 'tu líder'}.`
          );
        } else {
          setEditErrorMsg(
            `Excede el límite del requerimiento (${capErr.cap}h total). ` +
            `Disponible: ${capErr.remaining.toFixed(1)}h, intentaste usar: ${capErr.incoming.toFixed(1)}h.`
          );
        }
      } else {
        setEditErrorMsg(error.message || 'Error al guardar');
      }
      return;
    }

    if (count === 0) {
      // RLS silently rejected (log outside current week or already deleted)
      setEditErrorMsg('No se pudo guardar este cambio. Puede que el registro esté fuera de la semana actual o que no tengas permiso para modificarlo.');
      return;
    }

    setPendingEdit(null);
    setEditErrorMsg('');
    setSuccessMsg('Registro actualizado correctamente');
    setTimeout(() => setSuccessMsg(''), 4500);
    fetchExistingLogs();
  }

  async function handleConfirmDelete() {
    if (!pendingDelete) return;
    // Locked past day (no active grant) — blocked for Líder/Desarrollador
    if (!isGerente && lockedDays.has(pendingDelete.log_date)) {
      setDeleteErrorMsg('Este día ya pasó y está bloqueado. Solicita permiso a un Gerente para modificarlo.');
      return;
    }
    // For Desarrolladores, re-check the week hasn't rolled over since the modal opened
    if (!isManagerOrLeader && mondayOfWeek(new Date()).getTime() !== monday.getTime()) {
      setDeleteErrorMsg('La semana actual cambió. Ya no es posible eliminar registros de semanas anteriores.');
      return;
    }
    setDeleting(true);
    const { error, count } = await supabase
      .from('time_logs')
      .delete({ count: 'exact' })
      .eq('id', pendingDelete.id);
    setDeleting(false);
    if (error) {
      setDeleteErrorMsg(error.message || 'Error al eliminar');
      return;
    }
    if (count === 0) {
      // RLS rejected silently (e.g. log is now outside current week) or already deleted
      setDeleteErrorMsg('No se pudo eliminar este registro. Puede que ya haya sido eliminado o que no tengas permiso para modificar registros de semanas anteriores.');
      return;
    }
    setPendingDelete(null);
    setDeleteErrorMsg('');
    setSuccessMsg('Registro eliminado correctamente');
    setTimeout(() => setSuccessMsg(''), 4500);
    fetchExistingLogs();
  }

  const applyToCell = useCallback((date, slot, mode) => {
    if (lockedDays.has(date)) return;
    if (occupiedSlots.has(`${date}_${slot}`)) return;
    if (!selectedActivityId) return;
    if (isExtendedSlot(date, slot) && !extendedHoursAllowed) return;
    setBlocks((prev) => {
      const key = `${date}_${slot}`;
      const next = new Map(prev);
      if (mode === 'add') next.set(key, { activityId: selectedActivityId });
      else next.delete(key);
      return next;
    });
  }, [lockedDays, occupiedSlots, selectedActivityId, extendedHoursAllowed]);

  function handleCellDown(e, date, slot) {
    e.preventDefault();

    // Locked past day (Líder/Desarrollador, no active grant) — any action blocked
    if (lockedDays.has(date)) {
      setShowLockedModal(true);
      return;
    }

    const occupied = occupiedSlots.get(`${date}_${slot}`);

    if (calMode === 'delete') {
      if (occupied) {
        setDeleteErrorMsg('');
        setPendingDelete(occupied);
      }
      return;
    }

    if (calMode === 'edit') return; // handled by handle mousedown, not cell mousedown

    // --- create mode ---
    if (occupied) return; // occupied cells are read-only in create mode

    if (!selectedRequirementId) {
      if (isManagerOrLeader && !targetUserId) {
        setGuideModal('no-target-user');
      } else {
        setGuideModal(requirements.length === 0 ? 'no-assignments' : 'no-req');
      }
      return;
    }
    if (isExtendedSlot(date, slot) && !extendedHoursAllowed) {
      setShowExtBlockedModal(true);
      return;
    }
    if (!selectedActivityId) {
      setGuideModal('no-activity');
      return;
    }
    const key = `${date}_${slot}`;
    const existing = blocks.get(key);
    const mode = existing && existing.activityId === selectedActivityId ? 'remove' : 'add';
    paintModeRef.current = mode;
    setIsPainting(true);
    applyToCell(date, slot, mode);
  }

  function handleCellEnter(date, slot) {
    if (resizingLog) {
      // Only update preview if on the same day as the log being resized
      if (date === resizingLog.log_date) {
        setResizePreviewSlot(slot);
      }
      return;
    }
    if (!isPainting) return;
    applyToCell(date, slot, paintModeRef.current);
  }

  /* ── Build entries by grouping contiguous same-activity blocks ── */
  function buildEntries() {
    const byKey = new Map(); // `${date}_${activityId}` → slot[]
    for (const [k, v] of blocks) {
      const [date, slotStr] = k.split('_');
      const slot = Number(slotStr);
      const mapKey = `${date}_${v.activityId}`;
      if (!byKey.has(mapKey)) byKey.set(mapKey, []);
      byKey.get(mapKey).push(slot);
    }
    const entries = [];
    for (const [mapKey, slots] of byKey) {
      slots.sort((a, b) => a - b);
      const [date, actStr] = mapKey.split('_');
      const activityId = Number(actStr);
      const activity = activities.find((a) => a.id === activityId);
      let runStart = slots[0];
      for (let i = 0; i < slots.length; i++) {
        const isLast = i === slots.length - 1;
        const breakHere = !isLast && slots[i + 1] !== slots[i] + 1;
        if (isLast || breakHere) {
          const runEnd = slots[i];
          let normal = 0;
          let ext = 0;
          for (let s = runStart; s <= runEnd; s++) {
            if (isExtendedSlot(date, s)) ext += 0.5;
            else normal += 0.5;
          }
          entries.push({
            tempId: `${date}_${activityId}_${runStart}`,
            date,
            activityId,
            activity,
            startSlot: runStart,
            endSlot: runEnd,
            startTime: slotToTime(runStart),
            endTime: slotEndTime(runEnd + 1),
            totalHours: normal,
            extendedHours: ext,
            isExtended: ext > 0,
          });
          if (!isLast) runStart = slots[i + 1];
        }
      }
    }
    entries.sort((a, b) => a.date.localeCompare(b.date) || a.startSlot - b.startSlot);
    return entries;
  }

  /* ── Save flow ── */
  function handleSaveClick() {
    setErrorMsg('');
    if (blocks.size === 0) {
      setErrorMsg('No hay bloques seleccionados');
      return;
    }
    // Week check
    if (mondayOfWeek(new Date()).getTime() !== monday.getTime()) {
      setErrorMsg('La semana actual cambió. Recarga la vista.');
      return;
    }
    const entries = buildEntries();

    // Guard: no entries on a locked past day (e.g. a grant expired while painting)
    if (!isGerente && entries.some((e) => lockedDays.has(e.date))) {
      setErrorMsg('Uno o más bloques están en un día que ya pasó y está bloqueado. Solicita permiso a un Gerente.');
      return;
    }

    // Guard: re-validate extended hours in case allow_extended_hours was toggled by another user
    if (entries.some((e) => e.isExtended) && !extendedHoursAllowed) {
      setErrorMsg(
        isNoneRequirement
          ? 'Las actividades sin requerimiento no pueden registrarse fuera del horario laboral (9:00–18:00) ni en fin de semana.'
          : 'Este requerimiento no tiene autorización para registrar horas fuera del horario laboral.'
      );
      return;
    }

    const initialDescs = {};
    entries.forEach((e) => { initialDescs[e.tempId] = ''; });
    setDescriptions(initialDescs);
    setEntriesToSave(entries);
    setShowDescModal(true);
  }

  async function confirmSave() {
    setErrorMsg('');
    // Validate descriptions
    for (const e of entriesToSave) {
      if (!descriptions[e.tempId]?.trim()) {
        setErrorMsg('Completa la descripción de todos los registros');
        return;
      }
    }
    // Re-check current week (edge case: Sunday 23:59 → Monday 00:01)
    if (mondayOfWeek(new Date()).getTime() !== monday.getTime()) {
      setErrorMsg('La semana actual cambió antes de guardar. Recarga la vista.');
      return;
    }

    setSaving(true);

    // Re-fetch existing day totals + check 24h cap per day
    const uniqueDates = [...new Set(entriesToSave.map((e) => e.date))];
    const { data: existingForCheck, error: checkErr } = await supabase
      .from('time_logs')
      .select('log_date, total_hours, extended_hours, start_time, end_time')
      .eq('user_id', targetUserId)
      .in('log_date', uniqueDates);

    if (checkErr) {
      setSaving(false);
      setErrorMsg(checkErr.message || 'Error al validar');
      return;
    }

    const existingByDate = {};
    for (const row of existingForCheck || []) {
      existingByDate[row.log_date] =
        (existingByDate[row.log_date] || 0) + (row.total_hours || 0) + (row.extended_hours || 0);
    }
    const newByDate = {};
    for (const e of entriesToSave) {
      newByDate[e.date] = (newByDate[e.date] || 0) + e.totalHours + e.extendedHours;
    }
    for (const d of uniqueDates) {
      if ((existingByDate[d] || 0) + (newByDate[d] || 0) > 24) {
        setSaving(false);
        setErrorMsg(`Excede 24h en el día ${d} (${(existingByDate[d] || 0).toFixed(1)}h ya registradas)`);
        return;
      }
    }

    // Overlap re-check: painting only avoids the locally cached logs, which
    // may be stale if another session wrote logs for this user meanwhile.
    for (const e of entriesToSave) {
      const conflict = (existingForCheck || []).find((row) => {
        if (row.log_date !== e.date) return false;
        const oStart = timeToSlot(row.start_time);
        const oEnd = timeToSlot(row.end_time);
        return e.startSlot < oEnd && e.endSlot + 1 > oStart;
      });
      if (conflict) {
        setSaving(false);
        setErrorMsg(
          `El bloque del ${formatEntryDate(e.date)} (${e.startTime.slice(0, 5)}–${e.endTime.slice(0, 5)}) se superpone con un registro ya guardado (${conflict.start_time.slice(0, 5)}–${conflict.end_time.slice(0, 5)}). El calendario se actualizó; revisa los bloques pintados.`
        );
        fetchExistingLogs();
        return;
      }
    }

    // Requirement hours cap pre-check — only applies to Facturable activities
    if (!isNoneRequirement) {
      const req = requirements.find((r) => r.id === selectedRequirementId);
      if (req) {
        const facturableEntries = entriesToSave.filter(
          (e) => e.activity?.hour_types?.name === 'Facturables'
        );
        if (facturableEntries.length > 0) {
          if (req.estimated_completion_hours_dev === null || req.estimated_completion_hours_dev === undefined) {
            setSaving(false);
            setShowDescModal(false);
            setHoursCapModal({ type: 'null_cap', refId: req.reference_id, requirement: req });
            return;
          }
          const { data: reqLogs } = await supabase
            .from('time_logs')
            .select('total_hours, extended_hours, activity:activities!activity_id(hour_types(name))')
            .eq('requirement_id', selectedRequirementId);
          const consumed = (reqLogs || [])
            .filter((l) => l.activity?.hour_types?.name === 'Facturables')
            .reduce((sum, l) => sum + (l.total_hours || 0) + (l.extended_hours || 0), 0);
          const incoming = facturableEntries.reduce((sum, e) => sum + e.totalHours + e.extendedHours, 0);
          const cap = Number(req.estimated_completion_hours_dev);
          if (consumed + incoming > cap) {
            setSaving(false);
            setShowDescModal(false);
            setHoursCapModal({
              type: 'exceeded', refId: req.reference_id, requirement: req,
              cap, consumed, incoming, remaining: Math.max(0, cap - consumed),
            });
            return;
          }
        }
      }
    }

    // Build and insert payloads
    const payloads = entriesToSave.map((e) => ({
      user_id: targetUserId,
      log_date: e.date,
      activity_id: e.activityId,
      requirement_id: isNoneRequirement ? null : selectedRequirementId,
      start_time: e.startTime,
      end_time: e.endTime,
      total_hours: e.totalHours,
      is_extended_hours: e.isExtended,
      extended_hours: e.isExtended ? e.extendedHours : null,
      description: descriptions[e.tempId].trim(),
    }));

    const { error } = await supabase.from('time_logs').insert(payloads);
    setSaving(false);

    if (error) {
      const capErr = parseHoursCapError(error.message);
      if (capErr) {
        const req = requirements.find((r) => r.reference_id === capErr.refId);
        setShowDescModal(false);
        setHoursCapModal({ ...capErr, requirement: req || null });
      } else {
        setErrorMsg(error.message || 'Error al guardar');
      }
      return;
    }

    // Success — clear and refresh
    setShowDescModal(false);
    setBlocks(new Map());
    setSuccessMsg(`${entriesToSave.length} registro${entriesToSave.length !== 1 ? 's' : ''} guardado${entriesToSave.length !== 1 ? 's' : ''} correctamente`);
    setTimeout(() => setSuccessMsg(''), 4500);
    fetchExistingLogs();
  }

  /* ── Render ──────────────────────────────────── */
  const GUIDE_CONTENT = {
    'no-target-user': {
      title: 'Selecciona un recurso',
      message: 'Selecciona el recurso al que deseas registrarle actividades en el menú de la parte izquierda de la pantalla.',
    },
    'no-assignments': {
      title: 'Sin folios asignados',
      message: !isManagerOrLeader
        ? 'Parece que no tienes folios asignados aún. Por favor contacta a tu líder para que te asigne trabajo.'
        : 'Este recurso no tiene folios asignados en el sistema.',
    },
    'no-req': {
      title: 'Selecciona un requerimiento',
      message: 'No has seleccionado un requerimiento aún. Selecciónalo en el menú de la parte izquierda de la pantalla.',
    },
    'no-activity': {
      title: 'Selecciona una actividad',
      message: 'No has seleccionado una actividad. Puedes encontrarlas en el menú izquierdo, justo debajo de la lista de requerimientos.',
    },
  };

  return (
    <>
    <div className="act-cal-view">
      {/* Sub-header: back + week + mode switcher + actions */}
      <div className="act-cal-subheader">
        <div className="act-cal-subheader-left">
          <button type="button" className="cat-back-btn act-cal-back" onClick={onBack}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            Volver
          </button>
          <div className="act-cal-week">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
            {isGerente && (
              <button
                type="button"
                className="act-cal-week-nav-btn"
                onClick={() => handleWeekNav(-1)}
                disabled={isPrevWeek}
                title="Ver semana anterior"
                aria-label="Ver semana anterior"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
              </button>
            )}
            <span>{formatWeekRange(viewMonday)}</span>
            {isGerente && (
              <button
                type="button"
                className="act-cal-week-nav-btn"
                onClick={() => handleWeekNav(0)}
                disabled={!isPrevWeek}
                title="Volver a la semana actual"
                aria-label="Volver a la semana actual"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
              </button>
            )}
            {isPrevWeek && <span className="act-cal-week-badge">Semana anterior</span>}
          </div>
        </div>

        {/* Mode switcher */}
        <div className="act-cal-mode-switcher">
          <button
            type="button"
            className={`act-cal-mode-btn ${calMode === 'create' ? 'act-cal-mode-btn--active act-cal-mode-btn--create' : ''}`}
            onClick={() => handleModeSwitch('create')}
            disabled={showDescModal || !!pendingEdit}
            title="Crear bloques de actividad"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
            <span className="act-cal-mode-label">Crear</span>
          </button>
          <button
            type="button"
            className={`act-cal-mode-btn ${calMode === 'delete' ? 'act-cal-mode-btn--active act-cal-mode-btn--delete' : ''}`}
            onClick={() => handleModeSwitch('delete')}
            disabled={showDescModal || !!pendingEdit}
            title="Eliminar registros guardados"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>
            <span className="act-cal-mode-label">Eliminar</span>
          </button>
          <button
            type="button"
            className={`act-cal-mode-btn ${calMode === 'edit' ? 'act-cal-mode-btn--active act-cal-mode-btn--edit' : ''}`}
            onClick={() => handleModeSwitch('edit')}
            disabled={showDescModal || !!pendingEdit}
            title="Editar duración de registros guardados"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>
            <span className="act-cal-mode-label">Editar</span>
          </button>
        </div>

        <div className="act-cal-actions">
          {calMode === 'create' ? (
            <>
              <div className="act-cal-totals">
                <div className="act-cal-total">
                  <span className="act-cal-total-val">{totals.normal}h</span>
                  <span className="act-cal-total-label">Normales</span>
                </div>
                <div className={`act-cal-total ${totals.ext > 0 ? 'act-cal-total--ext' : ''}`}>
                  <span className="act-cal-total-val">{totals.ext}h</span>
                  <span className="act-cal-total-label">Extendidas</span>
                </div>
              </div>
              {blocks.size > 0 && (
                <button type="button" className="act-cal-clear-btn" onClick={clearAll}>
                  Limpiar
                </button>
              )}
              <button
                type="button"
                className="cat-btn-primary"
                onClick={handleSaveClick}
                disabled={blocks.size === 0}
              >
                Guardar {blocks.size > 0 && `(${blocks.size})`}
              </button>
            </>
          ) : (
            <span className="act-cal-mode-hint">
              {calMode === 'delete'
                ? 'Clic en un bloque guardado para eliminarlo'
                : 'Arrastra el borde de un bloque para redimensionarlo'}
            </span>
          )}
        </div>
      </div>

      {successMsg && <div className="act-banner act-banner--success">{successMsg}</div>}
      {errorMsg && !showDescModal && <div className="cat-error-banner">{errorMsg}</div>}
      {isPrevWeek && (
        <div className="act-extended-hint act-extended-hint--prev-week">
          <span>Estás viendo la <strong>semana anterior</strong>. Los cambios que crees, edites o elimines se registrarán en días pasados.</span>
        </div>
      )}
      {totals.ext > 0 && extendedHoursAllowed && (
        <div className="act-extended-hint">
          Hay bloques fuera del horario laboral (9:00–18:00) o en fin de semana. Se registrarán como horas extendidas al guardar.
        </div>
      )}

      {/* Body: sidebar + calendar */}
      <div className="act-cal-body">
        <aside className={`act-cal-sidebar${calMode !== 'create' ? ' act-cal-sidebar--dimmed' : ''}`}>
          {isManagerOrLeader && (
            <div className="act-cal-section act-cal-section--user-picker">
              <h3 className="act-cal-sec-title">Recurso</h3>
              <select
                className="cat-select"
                value={targetUserId || ''}
                onChange={(e) => handleUserChange(e.target.value)}
              >
                <option value="">Seleccionar recurso</option>
                {allUsers.map((u) => (
                  <option key={u.id} value={u.id}>{u.full_name}</option>
                ))}
              </select>
            </div>
          )}

          <div className="act-cal-section">
            <h3 className="act-cal-sec-title">
              <span className="act-cal-sec-num">1</span> Requerimiento
            </h3>
            <div className="act-cal-req-list">
              {requirements.length === 0 && (
                <span className="act-cal-empty">Sin requerimientos asignados</span>
              )}
              {requirements.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  className={`act-cal-req-btn ${selectedRequirementId === r.id ? 'act-cal-req-btn--active' : ''}`}
                  onClick={() => handleRequirementClick(r.id)}
                >
                  <span className="act-cal-req-ref">{r.reference_id}</span>
                  <span className="act-cal-req-desc">{r.description}</span>
                </button>
              ))}
              <button
                type="button"
                className={`act-cal-req-btn act-cal-req-btn--none ${selectedRequirementId === 'none' ? 'act-cal-req-btn--active' : ''}`}
                onClick={() => handleRequirementClick('none')}
              >
                <span className="act-cal-req-ref">Sin requerimiento</span>
                <span className="act-cal-req-desc">Solo actividades N/A</span>
              </button>
            </div>
          </div>

          <div className="act-cal-section">
            <h3 className="act-cal-sec-title">
              <span className="act-cal-sec-num">2</span> Actividad
            </h3>
            {!selectedRequirementId ? (
              <span className="act-cal-empty">Selecciona un requerimiento</span>
            ) : (
              <div className="act-cal-activity-list">
                {Object.entries(visibleActivityGroups).map(([group, acts]) => (
                  <div key={group} className="act-cal-act-group">
                    <span className="act-cal-group-label">{group}</span>
                    {acts.map((a) => {
                      const color = colorForActivity(a);
                      const active = selectedActivityId === a.id;
                      return (
                        <button
                          key={a.id}
                          type="button"
                          className={`act-cal-act-btn ${active ? 'act-cal-act-btn--active' : ''}`}
                          onClick={() => setSelectedActivityId(a.id)}
                          style={{ '--act-color': color }}
                        >
                          <span className="act-cal-act-swatch" />
                          <span className="act-cal-act-name">{a.name}</span>
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </div>

          {calMode === 'create' && (
            <div className="act-cal-section act-cal-help">
              <h3 className="act-cal-sec-title">
                <span className="act-cal-sec-num">3</span> Pinta bloques
              </h3>
              <p className="act-cal-help-text">
                Haz clic o arrastra sobre el calendario para seleccionar bloques de 30 minutos.
                Cambia de actividad para pintar de otro color.
              </p>
            </div>
          )}
          {calMode === 'delete' && (
            <div className="act-cal-section act-cal-help act-cal-help--delete">
              <h3 className="act-cal-sec-title">
                Modo Eliminar
              </h3>
              <p className="act-cal-help-text">
                Haz clic sobre cualquier bloque guardado (azul rayado) para eliminarlo. Se pedirá confirmación antes de borrar.
              </p>
            </div>
          )}
          {calMode === 'edit' && (
            <div className="act-cal-section act-cal-help act-cal-help--edit">
              <h3 className="act-cal-sec-title">
                Modo Editar
              </h3>
              <p className="act-cal-help-text">
                Arrastra el borde superior o inferior de un bloque guardado para ajustar su duración. Se pedirá confirmación antes de guardar.
              </p>
            </div>
          )}
        </aside>

        <div className="act-cal-main">
          <div
            className="act-cal-scroller"
            ref={scrollerRef}
            onTouchMove={(e) => {
              if (!resizingLog) return;
              e.preventDefault();
              const touch = e.touches[0];
              const el = document.elementFromPoint(touch.clientX, touch.clientY);
              const cell = el?.closest('[data-slot]');
              if (cell && cell.dataset.date === resizingLog.log_date) {
                setResizePreviewSlot(Number(cell.dataset.slot));
              }
            }}
          >
            <div
              className="act-cal-grid"
              style={{ gridTemplateRows: `auto repeat(${SLOTS_PER_DAY}, ${SLOT_HEIGHT}px)` }}
            >
              {/* Corner (row 1, col 1) */}
              <div className="act-cal-corner" />

              {/* Day headers (row 1, cols 2..8) */}
              {weekDates.map((d, i) => {
                const dh = dateStr(d);
                const isToday = dh === todayStrVal;
                const dayLocked = lockedDays.has(dh);
                return (
                  <div
                    key={`h${i}`}
                    className={`act-cal-day-header ${isToday ? 'act-cal-day-header--today' : ''} ${dayLocked ? 'act-cal-day-header--locked' : ''}`}
                    style={{ gridColumn: i + 2, gridRow: 1 }}
                    title={dayLocked ? 'Día bloqueado — ya pasó' : undefined}
                  >
                    <span className="act-cal-day-label">
                      {DAY_LABELS[i]}
                      {dayLocked && (
                        <svg className="act-cal-day-lock" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                        </svg>
                      )}
                    </span>
                    <span className="act-cal-day-num">{d.getDate()}</span>
                  </div>
                );
              })}

              {/* Hour labels (col 1, rows 2..49, spanning 2 rows) */}
              {Array.from({ length: 24 }).map((_, h) => (
                <div
                  key={`hl${h}`}
                  className="act-cal-hour-label"
                  style={{ gridColumn: 1, gridRow: `${h * 2 + 2} / span 2` }}
                >
                  {String(h).padStart(2, '0')}:00
                </div>
              ))}

              {/* Body cells */}
              {weekDates.map((d, dayIdx) => {
                const dstr = dateStr(d);
                return Array.from({ length: SLOTS_PER_DAY }).map((_, slot) => {
                  const key = `${dstr}_${slot}`;
                  const occupied = occupiedSlots.get(key);
                  const block = blocks.get(key);
                  const isExt = isExtendedSlot(dstr, slot);
                  const hourBoundary = slot % 2 === 1;
                  const isLocked = lockedDays.has(dstr);

                  const isExtBlocked = isExt && !occupied && !block && selectedRequirementId && !extendedHoursAllowed && !isLocked;

                  // Edit preview type for this cell
                  const editPreviewType = calMode === 'edit' ? (editPreviewSlots?.get(key) ?? null) : null;

                  let cls = 'act-cal-cell';
                  if (isExt) cls += ' act-cal-cell--ext';
                  if (isExtBlocked) cls += ' act-cal-cell--ext-blocked';
                  if (hourBoundary) cls += ' act-cal-cell--hour';
                  if (isLocked) cls += ' act-cal-cell--locked';
                  if (occupied) cls += ' act-cal-cell--occupied';
                  if (block) cls += ' act-cal-cell--painted';
                  if (!selectedActivityId && !occupied) cls += ' act-cal-cell--idle';
                  if (calMode === 'delete' && occupied) cls += ' act-cal-cell--deletable';
                  if (editPreviewType === 'expand') cls += ' act-cal-cell--edit-expand';
                  if (editPreviewType === 'trim') cls += ' act-cal-cell--edit-trim';

                  const activity = block ? activities.find((a) => a.id === block.activityId) : null;
                  const color = activity ? colorForActivity(activity) : null;

                  // Expand preview uses the resizing log's activity color
                  const expandColor = editPreviewType === 'expand' && resizingLog
                    ? colorForActivity(resizingLog.activity)
                    : null;

                  // Show activity label on first cell of a contiguous run
                  const prevKey = `${dstr}_${slot - 1}`;
                  const prevBlock = blocks.get(prevKey);
                  const showPaintedLabel =
                    block && (!prevBlock || prevBlock.activityId !== block.activityId);

                  const prevOcc = occupiedSlots.get(prevKey);
                  const showOccLabel = occupied && (!prevOcc || prevOcc.id !== occupied.id);

                  // Block boundaries for outlines (painted blocks)
                  const nextKey = `${dstr}_${slot + 1}`;
                  const nextBlock = blocks.get(nextKey);
                  const isBlockTop = block && (!prevBlock || prevBlock.activityId !== block.activityId);
                  const isBlockBottom = block && (!nextBlock || nextBlock.activityId !== block.activityId);

                  // Block boundaries for existing logs
                  const nextOcc = occupiedSlots.get(nextKey);
                  const isOccTop = occupied && (!prevOcc || prevOcc.id !== occupied.id);
                  const isOccBottom = occupied && (!nextOcc || nextOcc.id !== occupied.id);

                  if (isBlockTop) cls += ' act-cal-cell--block-top';
                  if (isBlockBottom) cls += ' act-cal-cell--block-bottom';
                  if (isOccTop) cls += ' act-cal-cell--occ-top';
                  if (isOccBottom) cls += ' act-cal-cell--occ-bottom';

                  // Tooltip
                  const occTooltip = occupied
                    ? calMode === 'delete'
                      ? `${occupied.activity?.name || 'Actividad'}${occupied.requirement ? `\n${occupied.requirement.reference_id} — ${occupied.requirement.description}` : '\nSin requerimiento'}\nClic para eliminar`
                      : calMode === 'edit'
                      ? `${occupied.activity?.name || 'Actividad'}${occupied.requirement ? `\n${occupied.requirement.reference_id} — ${occupied.requirement.description}` : '\nSin requerimiento'}\nArrastra un borde para editar`
                      : `${occupied.activity?.name || 'Actividad'}${occupied.requirement ? `\n${occupied.requirement.reference_id} — ${occupied.requirement.description}` : '\nSin requerimiento'}`
                    : undefined;

                  return (
                    <div
                      key={key}
                      data-date={dstr}
                      data-slot={slot}
                      className={cls}
                      style={{
                        gridColumn: dayIdx + 2,
                        gridRow: slot + 2,
                        backgroundColor: expandColor
                          ? hexToRgba(expandColor, 0.28)
                          : (color || undefined),
                        ...(expandColor ? { '--edit-border-color': expandColor } : {}),
                      }}
                      title={occTooltip}
                      onMouseDown={(e) => handleCellDown(e, dstr, slot)}
                      onMouseEnter={() => handleCellEnter(dstr, slot)}
                    >
                      {showPaintedLabel && activity && (
                        <span className="act-cal-cell-label">{activity.name}</span>
                      )}
                      {showOccLabel && (
                        <span className="act-cal-cell-label act-cal-cell-label--occ">
                          {occupied.activity?.name || 'Registrado'}
                        </span>
                      )}
                      {/* Edit handles — only in edit mode, only on block boundaries, not on locked days */}
                      {calMode === 'edit' && occupied && isOccTop && !isLocked && (
                        <div
                          className="act-cal-resize-handle act-cal-resize-handle--top"
                          onMouseDown={(e) => handleEditHandleDown(e, occupied, 'top')}
                          onTouchStart={(e) => handleEditHandleDown(e, occupied, 'top')}
                        />
                      )}
                      {calMode === 'edit' && occupied && isOccBottom && !isLocked && (
                        <div
                          className="act-cal-resize-handle act-cal-resize-handle--bottom"
                          onMouseDown={(e) => handleEditHandleDown(e, occupied, 'bottom')}
                          onTouchStart={(e) => handleEditHandleDown(e, occupied, 'bottom')}
                        />
                      )}
                    </div>
                  );
                });
              })}
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* ── Modals (rendered outside .act-cal-view so their fixed positioning
         resolves against the viewport, not the animated wrapper's transform
         containing block) ── */}

    {/* ── Requirement change confirm ── */}
    {pendingReqChange && (
        <>
          <div className="cat-backdrop" onClick={() => setPendingReqChange(null)} />
          <div className="cat-confirm">
            <h3 className="cat-confirm-title">¿Cambiar requerimiento?</h3>
            <p className="cat-confirm-msg">
              Se descartarán los {blocks.size} bloque{blocks.size !== 1 ? 's' : ''} pintado{blocks.size !== 1 ? 's' : ''}. Esta acción no se puede deshacer.
            </p>
            <div className="cat-confirm-actions">
              <button type="button" className="cat-btn-cancel" onClick={() => setPendingReqChange(null)}>
                Cancelar
              </button>
              <button type="button" className="cat-btn-danger" onClick={confirmRequirementChange}>
                Descartar y cambiar
              </button>
            </div>
          </div>
        </>
      )}

      {/* ── Mode change confirm (only when switching away from create with unsaved blocks) ── */}
      {pendingModeChange && (
        <>
          <div className="cat-backdrop" onClick={() => setPendingModeChange(null)} />
          <div className="cat-confirm">
            <h3 className="cat-confirm-title">¿Cambiar de modo?</h3>
            <p className="cat-confirm-msg">
              Se descartarán los {blocks.size} bloque{blocks.size !== 1 ? 's' : ''} pintado{blocks.size !== 1 ? 's' : ''} sin guardar. Esta acción no se puede deshacer.
            </p>
            <div className="cat-confirm-actions">
              <button type="button" className="cat-btn-cancel" onClick={() => setPendingModeChange(null)}>
                Cancelar
              </button>
              <button type="button" className="cat-btn-danger" onClick={applyModeSwitch}>
                Descartar y cambiar
              </button>
            </div>
          </div>
        </>
      )}

      {/* ── Week change confirm (Gerente, only when unsaved blocks exist) ── */}
      {pendingWeekChange !== null && (
        <>
          <div className="cat-backdrop" onClick={() => setPendingWeekChange(null)} />
          <div className="cat-confirm">
            <h3 className="cat-confirm-title">¿Cambiar de semana?</h3>
            <p className="cat-confirm-msg">
              Se descartarán los {blocks.size} bloque{blocks.size !== 1 ? 's' : ''} pintado{blocks.size !== 1 ? 's' : ''} sin guardar. Esta acción no se puede deshacer.
            </p>
            <div className="cat-confirm-actions">
              <button type="button" className="cat-btn-cancel" onClick={() => setPendingWeekChange(null)}>
                Cancelar
              </button>
              <button type="button" className="cat-btn-danger" onClick={() => applyWeekNav(pendingWeekChange)}>
                Descartar y cambiar
              </button>
            </div>
          </div>
        </>
      )}

      {/* ── Delete confirmation modal ── */}
      {pendingDelete && (
        <>
          <div className="cat-backdrop" onClick={() => !deleting && setPendingDelete(null)} />
          <div className="cat-confirm act-cal-delete-confirm">
            <h3 className="cat-confirm-title">Eliminar registro</h3>
            <p className="cat-confirm-msg">¿Seguro que deseas eliminar este registro de actividad? Esta acción no se puede deshacer.</p>

            <div className="act-cal-delete-detail">
              <div className="act-cal-delete-row">
                <span className="act-cal-delete-label">Actividad</span>
                <span className="act-cal-delete-val">
                  <span
                    className="act-cal-desc-swatch"
                    style={{ background: colorForActivity(pendingDelete.activity) }}
                  />
                  {pendingDelete.activity?.name || '—'}
                </span>
              </div>
              <div className="act-cal-delete-row">
                <span className="act-cal-delete-label">Fecha</span>
                <span className="act-cal-delete-val">{formatEntryDate(pendingDelete.log_date)}</span>
              </div>
              <div className="act-cal-delete-row">
                <span className="act-cal-delete-label">Horario</span>
                <span className="act-cal-delete-val">
                  {pendingDelete.start_time?.slice(0, 5)} – {pendingDelete.end_time?.slice(0, 5)}
                </span>
              </div>
              {(pendingDelete.total_hours > 0 || pendingDelete.extended_hours > 0) && (
                <div className="act-cal-delete-row">
                  <span className="act-cal-delete-label">Horas</span>
                  <span className="act-cal-delete-val">
                    {pendingDelete.total_hours > 0 && `${pendingDelete.total_hours}h normales`}
                    {pendingDelete.total_hours > 0 && pendingDelete.extended_hours > 0 && ' · '}
                    {pendingDelete.extended_hours > 0 && `${pendingDelete.extended_hours}h extendidas`}
                  </span>
                </div>
              )}
              <div className="act-cal-delete-row">
                <span className="act-cal-delete-label">Requerimiento</span>
                <span className="act-cal-delete-val">
                  {pendingDelete.requirement
                    ? `${pendingDelete.requirement.reference_id} — ${pendingDelete.requirement.description}`
                    : 'Sin requerimiento'}
                </span>
              </div>
              {isManagerOrLeader && targetUserId !== userId && (
                <div className="act-cal-delete-row">
                  <span className="act-cal-delete-label">Recurso</span>
                  <span className="act-cal-delete-val">
                    {allUsers.find((u) => u.id === targetUserId)?.full_name || targetUserId}
                  </span>
                </div>
              )}
            </div>

            {deleteErrorMsg && <div className="cat-error-banner" style={{ marginTop: '12px' }}>{deleteErrorMsg}</div>}

            <div className="cat-confirm-actions">
              <button
                type="button"
                className="cat-btn-cancel"
                onClick={() => { setPendingDelete(null); setDeleteErrorMsg(''); }}
                disabled={deleting}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="cat-btn-danger"
                onClick={handleConfirmDelete}
                disabled={deleting}
              >
                {deleting && <span className="cat-spinner" />}
                {deleting ? 'Eliminando...' : 'Eliminar registro'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* ── Edit (resize) confirmation modal ── */}
      {pendingEdit && (
        <>
          <div
            className="cat-backdrop"
            onClick={() => { if (!editSaving) { setPendingEdit(null); setEditErrorMsg(''); } }}
          />
          <div className="cat-confirm act-cal-edit-confirm">
            <h3 className="cat-confirm-title">Guardar cambio de horario</h3>
            <p className="cat-confirm-msg">Confirma el nuevo horario para este registro.</p>

            <div className="act-cal-edit-detail">
              <div className="act-cal-edit-row">
                <span className="act-cal-edit-label">Actividad</span>
                <span className="act-cal-edit-val">
                  <span
                    className="act-cal-desc-swatch"
                    style={{ background: colorForActivity(pendingEdit.log.activity) }}
                  />
                  {pendingEdit.log.activity?.name || '—'}
                </span>
              </div>
              <div className="act-cal-edit-row">
                <span className="act-cal-edit-label">Fecha</span>
                <span className="act-cal-edit-val">{formatEntryDate(pendingEdit.log.log_date)}</span>
              </div>
              <div className="act-cal-edit-row">
                <span className="act-cal-edit-label">Antes</span>
                <span className="act-cal-edit-val">
                  {pendingEdit.log.start_time?.slice(0, 5)} – {pendingEdit.log.end_time?.slice(0, 5)}
                  {' '}
                  <span className="act-cal-edit-hours">
                    ({((pendingEdit.log.total_hours || 0) + (pendingEdit.log.extended_hours || 0)).toFixed(1)}h)
                  </span>
                </span>
              </div>
              <div className="act-cal-edit-row">
                <span className="act-cal-edit-label">Después</span>
                <span className="act-cal-edit-val act-cal-edit-val--new">
                  {slotToTime(pendingEdit.newStartSlot).slice(0, 5)} – {slotEndTime(pendingEdit.newEndSlot + 1).slice(0, 5)}
                  {' '}
                  <span className="act-cal-edit-hours">
                    ({(pendingEdit.newTotalHours + pendingEdit.newExtendedHours).toFixed(1)}h
                    {pendingEdit.newExtendedHours > 0 && `, ${pendingEdit.newExtendedHours}h ext.`})
                  </span>
                </span>
              </div>
            </div>

            {editErrorMsg && <div className="cat-error-banner" style={{ marginTop: '12px' }}>{editErrorMsg}</div>}

            <div className="cat-confirm-actions">
              <button
                type="button"
                className="cat-btn-cancel"
                onClick={() => { setPendingEdit(null); setEditErrorMsg(''); }}
                disabled={editSaving}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="cat-btn-primary"
                onClick={handleConfirmEdit}
                disabled={editSaving}
              >
                {editSaving && <span className="cat-spinner" />}
                {editSaving ? 'Guardando...' : 'Guardar cambios'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* ── Extended hours blocked modal ── */}
      {showExtBlockedModal && (
        <>
          <div className="cat-backdrop" onClick={() => setShowExtBlockedModal(false)} />
          <div className="cat-confirm">
            <h3 className="cat-confirm-title">Horas fuera de horario</h3>
            <p className="cat-confirm-msg">
              {isNoneRequirement
                ? 'Las horas fuera del horario laboral (antes de las 9:00, después de las 18:00 o en fin de semana) no están autorizadas para actividades sin requerimiento.'
                : 'Las horas fuera del horario laboral (antes de las 9:00, después de las 18:00 o en fin de semana) no están autorizadas para este requerimiento.'}
            </p>
            <div className="cat-confirm-actions">
              <button
                type="button"
                className="cat-btn-primary"
                onClick={() => setShowExtBlockedModal(false)}
              >
                Entendido
              </button>
            </div>
          </div>
        </>
      )}

      {/* ── Locked past-day modal ── */}
      {showLockedModal && (
        <>
          <div className="cat-backdrop" onClick={() => setShowLockedModal(false)} />
          <div className="cat-confirm">
            <h3 className="cat-confirm-title">Día bloqueado</h3>
            <p className="cat-confirm-msg">
              No puedes crear, editar ni eliminar actividades en días que ya pasaron. Para modificar
              un día anterior de esta semana necesitas que un usuario con rol <strong>Gerente</strong> te
              otorgue un permiso temporal.
            </p>
            <div className="cat-confirm-actions">
              <button
                type="button"
                className="cat-btn-primary"
                onClick={() => setShowLockedModal(false)}
              >
                Entendido
              </button>
            </div>
          </div>
        </>
      )}

      {/* ── Guide modal (missing requirement / activity / target user) ── */}
      {guideModal && GUIDE_CONTENT[guideModal] && (
        <>
          <div className="cat-backdrop" onClick={() => setGuideModal(null)} />
          <div className="cat-confirm">
            <h3 className="cat-confirm-title">{GUIDE_CONTENT[guideModal].title}</h3>
            <p className="cat-confirm-msg">{GUIDE_CONTENT[guideModal].message}</p>
            <div className="cat-confirm-actions">
              <button
                type="button"
                className="cat-btn-primary"
                onClick={() => setGuideModal(null)}
              >
                Entendido
              </button>
            </div>
          </div>
        </>
      )}

      {/* ── Hours cap modal ── */}
      {hoursCapModal && (
        <>
          <div className="cat-backdrop" onClick={() => setHoursCapModal(null)} />
          <div className="cat-confirm act-cap-modal">
            {hoursCapModal.type === 'null_cap' ? (
              <>
                <h3 className="cat-confirm-title">Requerimiento sin horas estimadas</h3>
                <p className="cat-confirm-msg">
                  No es posible registrar actividades en el requerimiento{' '}
                  <strong>{hoursCapModal.refId}</strong>
                  {hoursCapModal.requirement?.description
                    ? ` — ${hoursCapModal.requirement.description}`
                    : ''}
                  {' '}porque aún no se le han asignado horas de desarrollo estimadas.
                </p>
                {hoursCapModal.requirement?.leader?.full_name && (
                  <p className="act-cap-contact">
                    Contacta a <strong>{hoursCapModal.requirement.leader.full_name}</strong>,
                    el líder de este requerimiento, para que asigne las horas disponibles.
                  </p>
                )}
              </>
            ) : (
              <>
                <h3 className="cat-confirm-title">Horas insuficientes en el requerimiento</h3>
                <p className="cat-confirm-msg">
                  No hay suficientes horas disponibles para registrar estas actividades en{' '}
                  <strong>{hoursCapModal.refId}</strong>
                  {hoursCapModal.requirement?.description
                    ? ` — ${hoursCapModal.requirement.description}`
                    : ''}.
                </p>
                <div className="act-cap-detail">
                  <div className="act-cap-row">
                    <span className="act-cap-label">Horas del requerimiento</span>
                    <span className="act-cap-val">{hoursCapModal.cap}h</span>
                  </div>
                  <div className="act-cap-row">
                    <span className="act-cap-label">Horas ya registradas</span>
                    <span className="act-cap-val">{hoursCapModal.consumed.toFixed(1)}h</span>
                  </div>
                  <div className="act-cap-row act-cap-row--available">
                    <span className="act-cap-label">Horas disponibles</span>
                    <span className="act-cap-val">{hoursCapModal.remaining.toFixed(1)}h</span>
                  </div>
                  <div className="act-cap-row act-cap-row--over">
                    <span className="act-cap-label">Intentas registrar</span>
                    <span className="act-cap-val">{hoursCapModal.incoming.toFixed(1)}h</span>
                  </div>
                </div>
                {hoursCapModal.remaining > 0 && (
                  <p className="act-cap-note">
                    Reduce los bloques pintados a {hoursCapModal.remaining.toFixed(1)}h o menos para poder guardar.
                  </p>
                )}
                {hoursCapModal.remaining <= 0 && (
                  <p className="act-cap-note">
                    Este requerimiento no tiene horas disponibles. Contacta al líder para que amplíe el estimado.
                  </p>
                )}
              </>
            )}
            <div className="cat-confirm-actions">
              <button
                type="button"
                className="cat-btn-primary"
                onClick={() => setHoursCapModal(null)}
              >
                Entendido
              </button>
            </div>
          </div>
        </>
      )}

      {/* ── Description modal ── */}
      {showDescModal && (
        <>
          <div className="cat-backdrop" onClick={() => !saving && setShowDescModal(false)} />
          <div className="cat-modal act-cal-desc-modal">
            <h2 className="cat-modal-title">Describe cada actividad</h2>
            <div className="cat-modal-form">
              <p className="act-cal-desc-sub">
                Ingresa una descripción para cada uno de los {entriesToSave.length} registro{entriesToSave.length !== 1 ? 's' : ''} a guardar.
              </p>

              <div className="act-cal-desc-scroll">
                {entriesToSave.map((e) => {
                  const color = colorForActivity(e.activity);
                  return (
                    <div key={e.tempId} className="act-cal-desc-entry">
                      <div className="act-cal-desc-entry-header">
                        <span className="act-cal-desc-swatch" style={{ background: color }} />
                        <div className="act-cal-desc-entry-info">
                          <span className="act-cal-desc-entry-act">{e.activity?.name}</span>
                          <span className="act-cal-desc-entry-meta">
                            {formatEntryDate(e.date)} · {e.startTime.slice(0, 5)}–{e.endTime.slice(0, 5)}
                            {e.totalHours > 0 && (
                              <span className="act-cal-desc-tag">{e.totalHours}h</span>
                            )}
                            {e.extendedHours > 0 && (
                              <span className="act-cal-desc-tag act-cal-desc-tag--ext">
                                +{e.extendedHours}h ext.
                              </span>
                            )}
                          </span>
                        </div>
                      </div>
                      <textarea
                        className="cat-input act-textarea"
                        placeholder="¿Qué se hizo durante este bloque?"
                        rows={2}
                        value={descriptions[e.tempId] || ''}
                        onChange={(ev) =>
                          setDescriptions((prev) => ({ ...prev, [e.tempId]: ev.target.value }))
                        }
                      />
                    </div>
                  );
                })}
              </div>

              {errorMsg && <div className="cat-error-banner">{errorMsg}</div>}

              <div className="cat-modal-actions">
                <button
                  type="button"
                  className="cat-btn-cancel"
                  onClick={() => setShowDescModal(false)}
                  disabled={saving}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="cat-btn-primary"
                  onClick={confirmSave}
                  disabled={saving}
                >
                  {saving && <span className="cat-spinner" />}
                  {saving ? 'Guardando...' : 'Confirmar y guardar'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default TimeLogForm;
